<script setup>
/**
 * 一个场景的链编辑，放在抽屉里：链编辑器 + 保存 + 被拒之后怎么办。
 *
 * 编辑缓冲区（`steps`）和库里的链（`props.mode.chain`）是两样东西：保存成功后父组件换进返回的那一行，
 * 两者相等，「未保存」自动消失；被拒（409 / 400）时缓冲区原封不动，错误条就在保存按钮上方，可直接重试。
 *
 * 「链不能空」的权威在后端配置校验；前端让它删不出来（`ChainEditor` 只剩一步时删除键 disabled）。
 * `enabled` 原样带回去：这里只编辑链，不顺手改别的列。
 */
import { computed, ref } from 'vue'
import { Save } from '@lucide/vue'
import { Badge, Button, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveLlmModeChain } from '../../../api/llm/index.js'
import { isChainDirty, toChainSaveBody, toEditableChain, validateChain } from '../mode-chain.js'
import ChainEditor from './ChainEditor.vue'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').ModeRow>} */
  mode: { type: Object, required: true },
  /** @type {import('vue').PropType<import('../mode-chain.js').ChainStep[]>} 可选的 (厂商, 模型) 全集。 */
  options: { type: Array, required: true },
})

const emit = defineEmits(['saved'])

const { t, te } = useI18n()
const purposeKey = computed(() => `visualGameAdmin.storyModes.${props.mode.mode}`)
const hasPurpose = computed(() => te(`${purposeKey.value}.name`))
const toast = useToast()

/** 编辑缓冲区；抽屉每次打开都重新挂载，从库里的链开始编辑。 */
const steps = ref(toEditableChain(props.mode.chain))
const saving = ref(false)
const saveError = ref('')

const validity = computed(() =>
  validateChain({ steps: steps.value, required: props.mode.required, options: props.options }),
)
const dirty = computed(() => isChainDirty(steps.value, props.mode.chain))
/** 有行没选完、或选了个已停用的 (厂商, 模型)：那不是配置，是没填完，不给存。 */
const hasStepError = computed(() => Object.keys(validity.value.steps).length > 0)

function reset() {
  steps.value = toEditableChain(props.mode.chain)
  saveError.value = ''
}

async function save() {
  saving.value = true
  saveError.value = ''
  try {
    const saved = await saveLlmModeChain(props.mode.mode, {
      ...toChainSaveBody(steps.value),
      enabled: props.mode.enabled,
    })
    toast.success(t('visualGameAdmin.llm.modes.toast.saved'))
    emit('saved', saved)
  } catch (err) {
    // 后端消息已经说清了是哪条不变量、改动是否回滚，原样显示，不改写成「保存失败」。
    saveError.value = err.message
    toast.error(t('visualGameAdmin.llm.modes.toast.failed'), { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div
    class="mode-editor"
    data-insp-path="apps/visual-game-admin/src/pages/llm/mode/ModeEditor.vue#mode"
  >
    <div class="mode-editor__meta">
      <code>{{ mode.mode }}</code>
      <Badge
        v-if="mode.required"
        variant="warning"
      >
        {{ t('visualGameAdmin.llm.modes.required') }}
      </Badge>
      <Badge
        v-if="!mode.enabled"
        variant="danger"
      >
        {{ t('visualGameAdmin.llm.modes.status.disabled') }}
      </Badge>
    </div>
    <p
      v-if="hasPurpose"
      class="mode-editor__description"
    >
      {{ t(`${purposeKey}.description`) }}
    </p>

    <ChainEditor
      :steps="steps"
      :options="options"
      :step-errors="validity.steps"
      @update:steps="steps = $event"
    />

    <div
      v-if="saveError"
      class="mode-editor__error"
      role="alert"
      data-insp-path="apps/visual-game-admin/src/pages/llm/mode/ModeEditor.vue#save-error"
    >
      <p class="vga-error">
        {{ saveError }}
      </p>
    </div>

    <div class="mode-editor__footer">
      <Badge
        v-if="dirty"
        variant="warning"
      >
        {{ t('visualGameAdmin.llm.modes.unsaved') }}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        :disabled="!dirty || saving"
        @click="reset"
      >
        {{ t('visualGameAdmin.llm.modes.actions.reset') }}
      </Button>
      <Button
        variant="primary"
        size="sm"
        :disabled="!dirty || hasStepError"
        :loading="saving"
        @click="save"
      >
        <Save :size="16" />
        {{ t(saveError ? 'visualGameAdmin.common.retry' : 'visualGameAdmin.llm.modes.actions.save') }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.mode-editor {
  display: grid;
  gap: var(--drama-space-lg);
}

.mode-editor__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.mode-editor__description {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  line-height: var(--drama-line-height-normal);
}

.mode-editor__error {
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
  color: var(--drama-color-danger);
}

.mode-editor__error p {
  margin: 0;
  overflow-wrap: anywhere;
}

.mode-editor__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--drama-space-sm);
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}
</style>
